// B-517: one working-paper mutation at a time. The engagement mutation guard refuses an overlapping write (409),
// so the editor never dispatches the next save before the previous one settles. Edits made while a save is in flight
// are merged into the next request; field_meta is merged per field so a Pending choice and a rationale both survive.

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function mergePatch(a = {}, b = {}) {
  const out = { ...a, ...b };
  if (a.field_meta || b.field_meta) {
    out.field_meta = { ...(a.field_meta || {}) };
    for (const [key, change] of Object.entries(b.field_meta || {})) out.field_meta[key] = { ...(out.field_meta[key] || {}), ...change };
  }
  return out;
}

// What is still unconfirmed after `sent` was saved: values changed again since dispatch stay.
export function subtractPatch(unsaved = {}, sent = {}) {
  const out = { ...unsaved };
  for (const key of Object.keys(sent)) if (key !== 'field_meta' && key in out && same(out[key], sent[key])) delete out[key];
  if (out.field_meta && sent.field_meta) {
    const meta = {};
    for (const [key, change] of Object.entries(out.field_meta)) {
      const rest = { ...change };
      for (const sub of Object.keys(sent.field_meta[key] || {})) if (sub in rest && same(rest[sub], sent.field_meta[key][sub])) delete rest[sub];
      if (Object.keys(rest).length) meta[key] = rest;
    }
    if (Object.keys(meta).length) out.field_meta = meta; else delete out.field_meta;
  }
  return out;
}

export function isEmptyPatch(patch) {
  return !patch || Object.keys(patch).length === 0;
}

// save(patch) queues a mergeable save; run(task) queues any other mutation (status, revision, review) in the same order.
// `send(patch, actions)` must resolve (never reject) with the saved document or {error}; `actions` are the original saves
// merged into `patch`, in order, so a refused request can be retried one user action at a time (never split inside one).
// Each caller is answered with its own result: `send` may resolve {perAction: [result per action]}, else all share one.
// save(patch, {seal: true}) is a request of its own: merged neither into an earlier one nor with a later one (an
// acknowledgement reaches the server apart from any change of what it acknowledges, as separate saves did before, so the
// server's clear-on-change rule applies and a refused acknowledgement cannot hold another edit).
export function createSerialSaver(send) {
  let chain = Promise.resolve();
  let next = null;
  const save = (patch, { seal = false } = {}) => {
    let answer;
    const own = new Promise((resolve) => { answer = resolve; });
    if (next && !next.sealed && !seal) {
      next.patch = mergePatch(next.patch, patch); next.actions.push(patch); next.answers.push(answer);
      return own;
    }
    const job = { patch, actions: [patch], answers: [answer], sealed: seal };
    job.promise = chain.then(() => { if (next === job) next = null; return send(job.patch, job.actions); });
    job.promise.then(
      (result) => job.answers.forEach((reply, i) => reply(Array.isArray(result?.perAction) ? result.perAction[i] : result)),
      (error) => job.answers.forEach((reply) => reply({ error })));
    chain = job.promise.catch(() => {});
    next = job;
    return own;
  };
  save.run = (task) => {
    next = null;
    const done = chain.then(task);
    chain = done.catch(() => {});
    return done;
  };
  // Resolves once nothing is queued or in flight, including work queued while waiting.
  save.idle = async () => {
    let seen;
    do { seen = chain; await seen; } while (seen !== chain);
  };
  return save;
}
